import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  TrendingUp, Plus, Search, Edit, 
  Calendar, AlertCircle, CheckCircle, User 
} from 'lucide-react';
import { timeManagementAPI, hcmAPI } from '../../../services/api';
import { toast } from 'sonner';

const EmployeeQuotas = () => {
  const navigate = useNavigate();
  const [quotas, setQuotas] = useState([]);
  const [quotaTypes, setQuotaTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotasRes, typesRes, employeesRes] = await Promise.all([
        timeManagementAPI.getEmployeeQuotas({ year: yearFilter }),
        timeManagementAPI.getAbsenceQuotaTypes(),
        hcmAPI.getEmployees()
      ]);
      setQuotas(quotasRes.data || []);
      setQuotaTypes(typesRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load quotas');
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredQuotas = quotas.filter(quota => {
    const matchesSearch = 
      (quota.employee_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quota.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Group quotas by employee
  const quotasByEmployee = filteredQuotas.reduce((acc, quota) => {
    const empId = quota.employee_id;
    if (!acc[empId]) {
      acc[empId] = {
        employee_id: empId,
        employee_name: quota.employee_name,
        quotas: []
      };
    }
    acc[empId].quotas.push(quota);
    return acc;
  }, {});

  const getUsageColor = (used, entitlement) => {
    const percentage = (used / entitlement) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getProgressBarColor = (used, entitlement) => {
    const percentage = (used / entitlement) * 100;
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="employee-quotas-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/time')}>Time Management</span>
            <span>/</span>
            <span>Employee Quotas</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employee Absence Quotas</h1>
              <p className="text-gray-600 mt-1">View and manage employee leave quotas and balances (IT2006)</p>
            </div>
            <Button onClick={() => { setEditingItem(null); setShowForm(true); }} data-testid="btn-assign-quota">
              <Plus className="h-4 w-4 mr-2" />
              Assign Quota
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <User className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Employees with Quotas</p>
                <p className="text-xl font-semibold">{Object.keys(quotasByEmployee).length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Quota Records</p>
                <p className="text-xl font-semibold">{quotas.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Quota Types</p>
                <p className="text-xl font-semibold">{quotaTypes.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Low Balance</p>
                <p className="text-xl font-semibold text-orange-600">
                  {quotas.filter(q => {
                    const remaining = q.entitlement + (q.carryover_from_previous || 0) + (q.adjustments || 0) - (q.used || 0);
                    return remaining < 5 && remaining >= 0;
                  }).length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {[2023, 2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Quota Cards by Employee */}
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : Object.keys(quotasByEmployee).length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            No quota records found for {yearFilter}. Click "Assign Quota" to add employee quotas.
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.values(quotasByEmployee).map((employee) => (
              <Card key={employee.employee_id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-700 font-medium">
                        {employee.employee_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{employee.employee_name}</h3>
                      <p className="text-sm text-gray-500">{employee.employee_id}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/hcm/employees/${employee.employee_id}`)}
                  >
                    View Employee
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employee.quotas.map((quota) => {
                    const total = quota.entitlement + (quota.carryover_from_previous || 0) + (quota.adjustments || 0);
                    const remaining = total - (quota.used || 0);
                    const usedPercentage = Math.min((quota.used / total) * 100, 100);
                    
                    return (
                      <div key={quota.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            {quota.quota_type_name || quota.quota_type_id}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setEditingItem(quota); setShowForm(true); }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Used</span>
                            <span className={`font-medium ${getUsageColor(quota.used, total)}`}>
                              {quota.used || 0} / {total} {quota.unit?.toLowerCase() || 'days'}
                            </span>
                          </div>
                          
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getProgressBarColor(quota.used, total)} transition-all`}
                              style={{ width: `${usedPercentage}%` }}
                            />
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Remaining</span>
                            <span className={`font-semibold ${remaining < 5 ? 'text-orange-600' : 'text-green-600'}`}>
                              {remaining.toFixed(1)} {quota.unit?.toLowerCase() || 'days'}
                            </span>
                          </div>
                          
                          {quota.carryover_from_previous > 0 && (
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Calendar className="h-3 w-3" />
                              Includes {quota.carryover_from_previous} carryover
                              {quota.carryover_expiry_date && (
                                <span> (expires {quota.carryover_expiry_date})</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <QuotaFormModal 
            quotaTypes={quotaTypes}
            employees={employees}
            editingItem={editingItem}
            year={yearFilter}
            onClose={() => { setShowForm(false); setEditingItem(null); }}
            onSave={() => { setShowForm(false); setEditingItem(null); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// Quota Form Modal
const QuotaFormModal = ({ quotaTypes, employees, editingItem, year, onClose, onSave }) => {
  const [formData, setFormData] = useState(editingItem || {
    employee_id: '',
    quota_type_id: '',
    year: year,
    entitlement: 0,
    used: 0,
    carryover_from_previous: 0,
    adjustments: 0,
    valid_from: `${year}-01-01`,
    valid_to: `${year}-12-31`,
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && (!formData.employee_id || !formData.quota_type_id)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingItem) {
        await timeManagementAPI.updateEmployeeQuota(editingItem.id, {
          entitlement: formData.entitlement,
          used: formData.used,
          carryover_from_previous: formData.carryover_from_previous,
          adjustments: formData.adjustments,
          notes: formData.notes
        });
        toast.success('Quota updated successfully');
      } else {
        await timeManagementAPI.createEmployeeQuota(formData);
        toast.success('Quota assigned successfully');
      }
      onSave();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {editingItem ? 'Edit Quota' : 'Assign Quota'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!editingItem && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.personal_data?.first_name} {emp.personal_data?.last_name} ({emp.employee_id})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quota Type *</label>
                <select
                  value={formData.quota_type_id}
                  onChange={(e) => {
                    const type = quotaTypes.find(t => t.quota_type_id === e.target.value);
                    setFormData({
                      ...formData, 
                      quota_type_id: e.target.value,
                      entitlement: type?.default_entitlement || 0
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select Type</option>
                  {quotaTypes.filter(t => t.status === 'Active').map(type => (
                    <option key={type.quota_type_id} value={type.quota_type_id}>
                      {type.name} ({type.default_entitlement} {type.unit})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          
          {editingItem && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Employee: <span className="font-medium">{editingItem.employee_name}</span></p>
              <p className="text-sm text-gray-600">Quota Type: <span className="font-medium">{editingItem.quota_type_name}</span></p>
              <p className="text-sm text-gray-600">Year: <span className="font-medium">{editingItem.year}</span></p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entitlement</label>
              <Input 
                type="number"
                value={formData.entitlement}
                onChange={(e) => setFormData({...formData, entitlement: parseFloat(e.target.value)})}
                step="0.5"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Used</label>
              <Input 
                type="number"
                value={formData.used}
                onChange={(e) => setFormData({...formData, used: parseFloat(e.target.value)})}
                step="0.5"
                min="0"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Carryover</label>
              <Input 
                type="number"
                value={formData.carryover_from_previous}
                onChange={(e) => setFormData({...formData, carryover_from_previous: parseFloat(e.target.value)})}
                step="0.5"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adjustments</label>
              <Input 
                type="number"
                value={formData.adjustments}
                onChange={(e) => setFormData({...formData, adjustments: parseFloat(e.target.value)})}
                step="0.5"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={2}
              placeholder="Additional notes"
            />
          </div>
          
          {/* Summary */}
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm font-medium text-orange-800">
              Total Available: {(formData.entitlement + (formData.carryover_from_previous || 0) + (formData.adjustments || 0)).toFixed(1)}
            </p>
            <p className="text-sm font-medium text-orange-800">
              Remaining: {(formData.entitlement + (formData.carryover_from_previous || 0) + (formData.adjustments || 0) - (formData.used || 0)).toFixed(1)}
            </p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeQuotas;
